import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import posthog from 'posthog-js';
import { User } from '../store/user/user.types';
import { AppConfigService } from './app-config.service';
import { isSuperUserOrDeveloper } from './usercapabilities.service';
import { BackendFeatureFlagsService } from './backend/backend-feature-flags.service';
import { ClientFeatureFlagName } from '../model/client-model/client-feature-flag';
import { filter } from 'rxjs';

declare var gtag: Function;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private _appConfigService = inject(AppConfigService);
  private _router = inject(Router);
  private _titleService = inject(Title);
  private _backendFeatureFlagsService = inject(BackendFeatureFlagsService);
  private _activatedRoute = inject(ActivatedRoute);

  private _googleAnalyticsEnabled: boolean | undefined = undefined;
  private _googleAnalyticsEventQueue: { a: any; b: any; c: any }[] = [];
  private _googleAnalyticsInitialised: boolean = false;
  private _googleAnalyticsTrackingID: string | undefined;
  private _isProduction: boolean = this._appConfigService.appConfig.environmentName === 'production';
  private _isPublicDashboard: boolean = false;
  private _isUserLoggedIn: boolean = false;
  private _isUserStaff: boolean = false;
  private _posthogAutoCapture: boolean = false;
  private _posthogCapturePerformance: boolean = false;

  constructor() {
    this._listenForRouteChanges();
  }

  sendPostHogEvent(eventName: string, properties?: Record<string, any>) {
    const shouldSendEvent = this._isProduction;
    if (!shouldSendEvent) {
      return;
    }

    posthog.capture(eventName, properties ?? undefined);
  }

  setBoard(boardId: string, boardTitle: string) {
    posthog.register({
      board_id: boardId,
      board_title: boardTitle,
    });

    if (!this._googleAnalyticsEnabled) {
      return;
    }

    this._gtag('set', 'dimension3', boardId);
    this._gtag('set', 'dimension4', boardTitle);
  }

  setTenant(tenantId: string) {
    posthog.register({ tenant_id: tenantId });
  }

  setUser(user: User | null) {
    if (!!user) {
      this._isUserLoggedIn = true;
      this._isUserStaff = isSuperUserOrDeveloper(user.globalRoles) || user.serviceProviders?.[0]?.role === 'SUPPORT';

      if (this._isUserStaff) {
        return;
      }

      posthog.identify(user.userId);
    } else {
      this._isUserLoggedIn = false;
      this._isUserStaff = false;
    }
  }

  private _configureGoogleAnalytics() {
    // Only enable Google Analytics for public dashboards with the PanoramaGA feature flag enabled
    const isPanoramaGAEnabled = this._backendFeatureFlagsService.isEnabled(ClientFeatureFlagName.PanoramaGA);
    const shouldEnable = this._isProduction && this._isPublicDashboard && isPanoramaGAEnabled;

    if (shouldEnable) {
      this._registerPageHit(this._router.url);
    } else {
      this._googleAnalyticsEventQueue = [];
    }

    this._googleAnalyticsEnabled = shouldEnable;
    if (this._googleAnalyticsEnabled && !this._googleAnalyticsInitialised) {
      this._googleAnalyticsInitialised = true;
      this._initialiseGoogleAnalytics();
      setTimeout(() => {
        this._processEventQueue(); // The queue can only contain data if we were waiting for the initial "set enabled" call
      }, 10);
    }
  }

  private _configurePosthog() {
    // Always enable Posthog for all routes, but configure appropriately
    const autoCapture = this._isProduction && this._isPublicDashboard;
    const capturePerformance = this._isProduction && this._isUserLoggedIn && !this._isUserStaff;

    const shouldToggleAutoCapture = this._posthogAutoCapture !== autoCapture;
    const shouldToggleCapturePerformance = this._posthogCapturePerformance !== capturePerformance;
    const shouldUpdateConfig = shouldToggleAutoCapture || shouldToggleCapturePerformance;

    if (!shouldUpdateConfig) {
      return;
    }

    posthog.set_config({
      autocapture: autoCapture,
      capture_pageview: autoCapture,
      capture_performance: capturePerformance,
    });

    this._posthogAutoCapture = autoCapture;
    this._posthogCapturePerformance = capturePerformance;
  }

  private _gtag(a: any, b: any, c: any) {
    if (!this._googleAnalyticsEnabled && !this._googleAnalyticsInitialised) {
      return;
    }

    this._googleAnalyticsEventQueue.push({ a, b, c });
    gtag(a, b, c);
  }

  private _initialiseGoogleAnalytics() {
    this._googleAnalyticsTrackingID = this._appConfigService.appConfig.googleAnalytics.trackingID;

    try {
      const script1 = document.createElement('script');
      script1.async = true;

      script1.src = 'https://www.googletagmanager.com/gtag/js?id=' + this._googleAnalyticsTrackingID;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${this._googleAnalyticsTrackingID}', {'send_page_view': false, 'anonymize_ip': true});
      `;
      document.head.appendChild(script2);
    } catch (ex) {
      console.error('📈 Error appending Google Analytics scripts', ex);
    }
  }

  private _getBoardIdFromRoute(): string | null {
    // Traverse the route tree to find the deepest active route
    // This is necessary because in Angular's routing, parameters are often in child routes
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const boardId = route.snapshot.paramMap.get('boardId');

    return boardId;
  }

  private _loadFeatureFlagsAndConfigure(url: string) {
    this._isPublicDashboard = url.includes('public');

    // Configure Posthog first (for all routes)
    this._configurePosthog();

    if (this._isPublicDashboard) {
      const boardId = this._getBoardIdFromRoute();

      if (boardId) {
        // For public dashboards, load feature flags first and then decide about Google Analytics
        this._backendFeatureFlagsService.loadFlagsForEditorOrPublicRoute(boardId).subscribe(() => {
          // Configure Google Analytics (only for public dashboard with PanoramaGA feature flag)
          this._configureGoogleAnalytics();
        });
      }
    } else {
      // For non-public dashboards, explicitly disable Google Analytics
      this._googleAnalyticsEnabled = false;
    }

    // Track page view in Posthog for all routes
    posthog.capture('$pageview', {
      url: url,
      page_title: this._titleService.getTitle(),
    });
  }

  private _listenForRouteChanges() {
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this._loadFeatureFlagsAndConfigure(event.urlAfterRedirects);
    });
  }

  private _processEventQueue(): void {
    this._googleAnalyticsEventQueue.forEach((e) => this._gtag(e.a, e.b, e.c));
    this._googleAnalyticsEventQueue = [];
  }

  private _registerPageHit(url: string) {
    this._gtag('config', this._googleAnalyticsTrackingID, {
      page_path: url,
      page_title: this._titleService.getTitle(),
    });
  }
}
